import { Input } from '@/components/ui/elements/input';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
  useFormAutosave,
} from '@/components/ui/form/form';
import { cn } from '@/utils/format';
import { ClassProp } from 'cva/dist/types';
import { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

type FormFileInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> &
  ClassProp & {
    description?: string;
    placeholder?: string;
    onFieldFocus?: (value?: string) => void;
    onFieldBlur?: (value: string) => void;
    onFileSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  };

export const FormFileInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  placeholder,
  description,
  className,
  onFieldFocus,
  onFieldBlur,
  onFileSelect,
  ...props
}: FormFileInputProps<TFieldValues, TName>) => {
  const autosave = useFormAutosave();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur, ...field } }) => (
        <FormItem>
          <FormControl>
            <Input
              id={name}
              placeholder={placeholder}
              type="file"
              aria-invalid={control?._formState.errors[name] ? 'true' : 'false'}
              className={cn(
                'm-0 block w-full min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.56rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:me-3 hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-hidden dark:border-neutral-600 dark:text-neutral-200 dark:file:bg-neutral-700 dark:file:text-neutral-100 dark:focus:border-primary',
                className,
              )}
              onFocus={(event) => {
                if (onFieldFocus) onFieldFocus(event.target.value);
                if (autosave.enabled) autosave.setPaused(true);
              }}
              onChange={(event) => {
                if (event.target.files) onChange(event.target.files[0]);
                if (onFileSelect) onFileSelect(event);
              }}
              onBlur={(event) => {
                if (onFieldBlur) onFieldBlur(event.target.value);
                if (autosave.enabled) autosave.setPaused(false);
                onBlur();
              }}
              value={value?.fileName}
              {...field}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
      {...props}
    />
  );
};
